/**
 * PatientConfirmDetails — the patient-facing "Confirm Details" card.
 *
 * The patient analogue of the physician onboarding "Confirm Details" step
 * (physician-ui-app/src/pages/PhysicianOnboarding.jsx): a single white card
 * with the Parseo mosaic header, a 3-step progress stepper, a green
 * "loaded from your provider" banner, and a two-column grid of always-editable
 * inputs. It replaces BOTH legacy patient confirm surfaces — the lightweight
 * "Quick check" (ProfileVerifyCard) and the "Review your information"
 * (ProfileEditor) mandatory step — with one consistent screen.
 *
 * Shown at the VERIFY stage of the bridge flow once the Mac has preloaded a
 * profile. The patient reviews/edits demographics, confirms their private
 * insurance (provider + plan/policy/group # + member/certificate ID), and
 * answers whether they ALSO have provincial drug coverage (Pharmacare etc.).
 * These are captured for every patient because nearly every PA form asks for
 * them.
 *
 * Contract with the parent (BridgeOnboardingPage):
 *   - `macProfile`  (snake_case dict) — the decrypted preloaded profile.
 *   - `verifiedDob` (string|null)     — DOB confirmed at the challenge step;
 *                                       used as a fallback when the profile
 *                                       lacks date_of_birth.
 *   - `onConfirm(mergedSnakeProfile)` — fires on "Continue". `mergedSnakeProfile`
 *                                       is the full snake_case dict (preloaded
 *                                       + edits) with the structured `insurance`
 *                                       sub-doc and flat keys kept in sync, so
 *                                       _macProfileToWizardProfile carries every
 *                                       edit through to submission.
 *
 * Inline styles only, to match the rest of patient-ui-app. No Tailwind, no
 * icon library — the mosaic comes from ParseoLogo and the stepper checks are
 * inline SVG.
 */

import React, { useMemo, useState } from "react";
import type { PatientProfile, PatientInsurance } from "../../types/shared";
import ParseoLogo from "../../shared/ui/ParseoLogo";
import InsuranceCardCapture from "./InsuranceCardCapture";
import AddressAutocompleteInput from "../../shared/ui/AddressAutocompleteInput";
import {
  FORMATS,
  formatValue,
  maskedChange,
  isFormatValid,
  validationMessage,
  suggestDomainTypo,
} from "../../shared/lib/inputFormats";
import {
  insuranceLabels,
  routeCandidates,
  buildInsuranceSubDoc,
} from "../../lib/insuranceConfirm";
import { planMemberDetailsComplete, planMemberTextOnly } from "../../lib/planMemberConfirm";

// Every wired-format field must be empty-or-valid to continue. health_card_ns
// is excluded (out-of-province cards warn but never block) per user decision.
const FORMAT_FIELDS: Record<string, string> = {
  date_of_birth: "date_iso",
  phone: "phone_na",
  postal_code: "postal_ca",
  email: "email",
  plan_member_dob: "date_iso",
  plan_member_email: "email",
  plan_member_phone: "phone_na",
};

// Patient-facing names for the format-gated boxes, used by the footer when the
// Continue button is blocked.
const FIELD_LABELS: Record<string, string> = {
  date_of_birth: "Date of Birth",
  phone: "Phone",
  postal_code: "Postal Code",
  email: "Email",
  plan_member_dob: "the plan member's Date of Birth",
  plan_member_email: "the plan member's Email",
  plan_member_phone: "the plan member's Mobile",
};

// Seed state through the display formatter. Inputs RENDER formatValue(raw)
// while the gate VALIDATES raw, so a stored value differing from its display
// form only by punctuation ("B3H1A1" vs "B3H 1A1") produced a correct-looking
// box, no inline error (those need `touched`), and a permanently disabled
// Continue under "Please fix the highlighted fields" — with nothing on screen
// to fix. 7 of 32 pilot profiles sat in exactly that dead end. Normalising on
// seed makes raw === displayed from the first render, and keeps the invariant
// inputFormats states: values are STORED in display form.
function normalizeFormats(vals: Record<string, string>): Record<string, string> {
  const out = { ...vals };
  for (const k of Object.keys(FORMAT_FIELDS)) {
    if (out[k]) out[k] = String(formatValue(FORMAT_FIELDS[k], out[k]) ?? "");
  }
  return out;
}

const PROVINCES = ["NS", "NB", "PE", "NL", "ON", "QC", "BC", "AB", "SK", "MB", "NT", "YT", "NU"];

const GENDER_OPTIONS = ["Female", "Male", "Other", "Prefer not to say"];

// Relationship to the insurance plan member (policyholder). Lowercase values
// match the backend / SendFormModal convention; when not "self" the plan
// member's own signature is required and they get their own secure link.
const RELATIONSHIP_OPTIONS = [
  { v: "self", label: "I am the plan member" },
  { v: "spouse", label: "Spouse / partner" },
  { v: "parent_guardian", label: "Parent / guardian" },
  { v: "other", label: "Other" },
];

// The patient's journey, mirroring the physician's 3-step stepper. This card
// owns step 0; the form-fill + signature steps live downstream in
// BridgeFieldsForm. The stepper is a visual cue, not a controller.
const STEPS = ["Confirm Details", "Your Form", "Review & Sign"];

const C = {
  navy: "#182a33",
  sage: "#6b9c8e",
  tan: "#d8c9b6",
  cream: "#f5f0eb",
  border: "#e6dccb",
  muted: "#5b6e7c",
  edited: "#c9772a",
  editedBg: "rgba(230, 140, 50, 0.08)",
  white: "#ffffff",
};

const labelStyle: React.CSSProperties = { display: "block", fontSize: 14, fontWeight: 700, color: C.navy, marginBottom: 6 };

// Province may arrive as a full name; the <select> options are codes.
const PROVINCE_NAME_TO_CODE: Record<string, string> = {
  "newfoundland and labrador": "NL", newfoundland: "NL", labrador: "NL",
  "prince edward island": "PE", "nova scotia": "NS", "new brunswick": "NB",
  quebec: "QC", "québec": "QC", ontario: "ON", manitoba: "MB",
  saskatchewan: "SK", alberta: "AB", "british columbia": "BC",
  yukon: "YT", "northwest territories": "NT", nunavut: "NU",
};
function toProvinceCode(v: unknown): string {
  if (!v) return "";
  const s = String(v).trim();
  if (PROVINCES.includes(s.toUpperCase())) return s.toUpperCase();
  return PROVINCE_NAME_TO_CODE[s.toLowerCase()] || s;
}

interface PatientConfirmDetailsProps {
  macProfile?: PatientProfile | null;
  verifiedDob?: string | null;
  onConfirm?: (mergedSnakeProfile: PatientProfile) => void;
}

interface FieldArgs {
  k: string;
  label?: React.ReactNode;
  type?: string;
  placeholder?: string;
  helper?: React.ReactNode;
  inputMode?: React.HTMLAttributes<HTMLInputElement>["inputMode"];
  full?: boolean;
  format?: string;
}

export default function PatientConfirmDetails({ macProfile, verifiedDob, onConfirm }: PatientConfirmDetailsProps) {
  const initial = useMemo<Record<string, string>>(() => {
    const p: Partial<PatientProfile> = macProfile || {};
    const ins: PatientInsurance = p.insurance || {};
    return normalizeFormats({
      first_name: p.first_name || "",
      last_name: p.last_name || "",
      date_of_birth: p.date_of_birth || verifiedDob || "",
      gender: p.gender || p.sex || "",
      health_care_number: p.health_care_number || "",
      phone: p.phone || "",
      email: p.email || "",
      address: p.address || "",
      suite: p.suite || "",
      city: p.city || "",
      province: toProvinceCode(p.province || "NS"),
      postal_code: p.postal_code || "",
      // Insurance — prefer the structured sub-doc, fall back to flat keys.
      insurance_provider: ins.provider || p.insurance_provider || "",
      plan_number: (ins.plan_id || ins.plan_number || p.plan_number || p.policy_number || "") as string,
      insurance_id: ins.member_id || p.insurance_id || "",
      // Secondary plan-level division/group id — only surfaced for insurers
      // that declare a division concept. Falls back to a legacy group_id.
      division_id: ins.division_id || ins.group_id || "",
      // Provincial drug coverage (Pharmacare etc.). Stored as the existing
      // has_public_insurance boolean. "" = unanswered.
      has_public_insurance:
        p.has_public_insurance === true ? "Yes" : p.has_public_insurance === false ? "No" : "",
      // Plan member (policyholder) — when the patient isn't the plan member,
      // these capture who is. Drives the split-link / patient-relay dispatch:
      // the plan member gets their own link to sign. Prefilled if a prior
      // session already recorded them.
      plan_member_relationship: (p.plan_member_relationship || "self").toLowerCase(),
      plan_member_first_name: p.plan_member_first_name || "",
      plan_member_last_name: p.plan_member_last_name || "",
      plan_member_email: p.plan_member_email || "",
      plan_member_phone: p.plan_member_phone || "",
      plan_member_dob: p.plan_member_dob || "",
    });
  }, [macProfile, verifiedDob]);

  const [values, setValues] = useState(initial);
  const [touched, setTouched] = useState<Record<string, boolean>>({});

  // Insurer-specific printed labels once the carrier is recognised (e.g.
  // Manulife → "Certificate number"). Also tells us whether to surface the
  // division/group input — only insurers that declare a division concept get
  // it, so most patients never see a third insurance box.
  const insLabels = useMemo(
    () => insuranceLabels(values.insurance_provider),
    [values.insurance_provider]
  );
  const showDivision = insLabels.declaresDivision;

  const editedKeys = useMemo(
    () => Object.keys(initial).filter((k) => String(values[k] ?? "") !== String(initial[k] ?? "")),
    [values, initial]
  );

  function set(key: string, value: string) {
    setTouched((t) => ({ ...t, [key]: true }));
    setValues((v) => ({ ...v, [key]: value }));
  }

  const emailValid =
    !values.email || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(values.email).trim());
  const emailTypoSuggestion = suggestDomainTypo(values.email);

  // Keep the offending keys, not just a boolean — the footer names them, so a
  // blocked patient is told which box to look at instead of being handed a
  // generic "date, phone, postal, or email" and a dead button.
  const badFormatKeys = Object.keys(FORMAT_FIELDS).filter(
    (k) => !isFormatValid(FORMAT_FIELDS[k], values[k])
  );
  const formatsOk = badFormatKeys.length === 0;
  const requiredOk =
    values.first_name.trim() && values.last_name.trim() && values.date_of_birth.trim()
    && (values.plan_number || "").trim();
  // When the patient isn't the plan member, we need the plan member's name +
  // DOB and ONE way to send them their own signing link — an email or a
  // mobile (case imp_5585d00d: email-only left a patient whose plan member
  // has no email with no way to continue). Rule: lib/planMemberConfirm.ts.
  const planMemberDistinct = values.plan_member_relationship !== "self";
  const planMemberEmailValid =
    !values.plan_member_email ||
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(values.plan_member_email).trim());
  const planMemberOk = !planMemberDistinct || planMemberDetailsComplete(values);
  const pmTextOnly = planMemberDistinct && planMemberTextOnly(values);
  const canContinue = requiredOk && emailValid && planMemberOk && formatsOk;

  function handleContinue() {
    if (!canContinue) return;
    const merged = {
      ...(macProfile || {}),
      first_name: values.first_name.trim(),
      last_name: values.last_name.trim(),
      date_of_birth: values.date_of_birth.trim(),
      gender: values.gender,
      health_care_number: values.health_care_number.trim(),
      phone: values.phone.trim(),
      email: values.email.trim(),
      address: values.address.trim(),
      suite: values.suite.trim(),
      city: values.city.trim(),
      province: values.province,
      postal_code: values.postal_code.trim(),
      // Keep the structured insurance sub-doc authoritative + mirror the flat
      // keys so every downstream reader (auto-fill, _macProfileToWizardProfile)
      // sees the patient's edits. buildInsuranceSubDoc is ADDITIVE — it keeps
      // provider/plan_number/member_id exactly and adds canonical plan_id/
      // division_id/plans[]; an old bridge ignores the new keys.
      insurance: buildInsuranceSubDoc((macProfile && macProfile.insurance) || {}, {
        insurance_provider: values.insurance_provider.trim(),
        plan_number: values.plan_number.trim(),
        insurance_id: values.insurance_id.trim(),
        division_id: String(values.division_id || "").trim(),
      }),
      insurance_provider: values.insurance_provider.trim(),
      plan_number: values.plan_number.trim(),
      insurance_id: values.insurance_id.trim(),
      has_public_insurance:
        values.has_public_insurance === "Yes"
          ? true
          : values.has_public_insurance === "No"
          ? false
          : (macProfile ? macProfile.has_public_insurance : undefined) ?? null,
      // Plan member — persisted so SendFormModal prefills next time and the
      // backend can dispatch the plan member their signing link. When the
      // patient IS the plan member, clear the identity fields so a stale prior
      // plan member can't linger.
      plan_member_relationship: values.plan_member_relationship,
      plan_member_first_name: planMemberDistinct ? values.plan_member_first_name.trim() : "",
      plan_member_last_name: planMemberDistinct ? values.plan_member_last_name.trim() : "",
      plan_member_email: planMemberDistinct ? values.plan_member_email.trim() : "",
      plan_member_phone: planMemberDistinct ? values.plan_member_phone.trim() : "",
      plan_member_dob: planMemberDistinct ? values.plan_member_dob.trim() : "",
    };
    onConfirm?.(merged as unknown as PatientProfile);
  }

  // ── styles ────────────────────────────────────────────────────────────
  const inputStyle = (key: string): React.CSSProperties => {
    const edited = editedKeys.includes(key);
    return {
      width: "100%",
      padding: "11px 14px",
      fontSize: 15,
      border: `1.5px solid ${edited ? C.edited : C.tan}`,
      borderRadius: 10,
      outline: "none",
      boxSizing: "border-box",
      color: C.navy,
      backgroundColor: edited ? C.editedBg : C.white,
      fontFamily: "system-ui",
    };
  };

  // Render helper — INVOKED as a function (`{field({...})}`), never mounted as
  // a <Component/>. Defining a component inside render and mounting it would
  // recreate its type every keystroke and blow away input focus; calling it
  // just returns host elements that reconcile in place. It closes over
  // `values`/`set` for the current render.
  // When `format` names a FORMATS key the input becomes an as-you-type masked
  // text field (value shown through the display formatter, onChange via
  // maskedChange, inputMode/maxLength/placeholder/autoComplete from the
  // registry, date→text). A non-empty invalid value shows an inline message;
  // health_card_ns warns only (out-of-province cards) and never blocks.
  const field = ({ k, label, type = "text", placeholder, helper, inputMode, full, format }: FieldArgs) => {
    const fmt = format && FORMATS[format] ? format : null;
    const rawVal = values[k];
    const displayVal = fmt ? formatValue(fmt, rawVal) : rawVal;
    // Show the inline error for a PREFILLED invalid value too, not just after
    // the patient types. Gating purely on `touched` meant a bad value that
    // arrived from the profile disabled Continue while highlighting nothing —
    // the message said "fix the highlighted fields" and none were.
    const showError =
      fmt && !isFormatValid(fmt, rawVal) && (touched[k] || rawVal === initial[k]);
    const isHcn = fmt === "health_card_ns";
    return (
      <div key={k} style={{ gridColumn: full ? "1 / -1" : "auto" }}>
        <label style={labelStyle}>
          {label}
          {editedKeys.includes(k) && (
            <span style={{ marginLeft: 8, fontSize: 11, fontWeight: 600, color: C.edited }}>edited</span>
          )}
        </label>
        {fmt ? (
          <input
            type={type === "date" ? "text" : type}
            value={displayVal as string}
            onChange={(e) => maskedChange(fmt, displayVal, e, (v) => set(k, v))}
            placeholder={placeholder || FORMATS[fmt].placeholder}
            inputMode={FORMATS[fmt].inputMode}
            maxLength={FORMATS[fmt].maxLength}
            autoComplete={type === "date" ? "bday" : FORMATS[fmt].autoComplete}
            style={inputStyle(k)}
          />
        ) : (
          <input
            type={type}
            value={values[k]}
            onChange={(e) => set(k, e.target.value)}
            placeholder={placeholder}
            inputMode={inputMode}
            style={inputStyle(k)}
          />
        )}
        {showError && (
          <p style={{ color: isHcn ? C.muted : "#c25450", fontSize: 12, margin: "5px 2px 0", lineHeight: 1.4 }}>
            {validationMessage(fmt, rawVal)}
          </p>
        )}
        {helper && <p style={{ color: C.muted, fontSize: 12, margin: "5px 2px 0", lineHeight: 1.4 }}>{helper}</p>}
      </div>
    );
  };

  return (
    <div
      className="hf-confirm-root"
      style={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 16,
        background: "linear-gradient(135deg, #f5f0eb 0%, #e4ecee 100%)",
        boxSizing: "border-box",
      }}
    >
      <style>{`
        .hf-confirm-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; }
        .hf-confirm-grid-3 { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 16px; }
        @media (max-width: 560px) {
          .hf-confirm-grid, .hf-confirm-grid-3 { grid-template-columns: 1fr; }
          .hf-confirm-root input, .hf-confirm-root select { font-size: 16px !important; min-height: 44px; }
          .hf-confirm-steplabel { display: none; }
        }
      `}</style>

      <div style={{ width: "100%", maxWidth: 560 }}>
        <div style={{ backgroundColor: C.white, borderRadius: 18, boxShadow: "0 10px 40px rgba(0,0,0,0.10)", overflow: "hidden" }}>
          {/* Header */}
          <div style={{ padding: "24px 32px", borderBottom: `1px solid ${C.border}`, backgroundColor: C.cream }}>
            <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 6 }}>
              <ParseoLogo size={36} />
              <div>
                <h1 style={{ fontSize: 18, fontWeight: 700, color: C.navy, margin: 0 }}>Welcome to Parseo Health</h1>
                <p style={{ fontSize: 13, color: C.muted, margin: 0 }}>Let's confirm your details for this form</p>
              </div>
            </div>

            {/* Progress stepper */}
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 16, flexWrap: "wrap" }}>
              {STEPS.map((title, i) => (
                <div key={title} style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <div
                    style={{
                      width: 28, height: 28, borderRadius: "50%",
                      display: "flex", alignItems: "center", justifyContent: "center",
                      fontSize: 12, fontWeight: 700,
                      backgroundColor: i === 0 ? C.navy : C.border,
                      color: i === 0 ? "#fff" : C.muted,
                    }}
                  >
                    {i + 1}
                  </div>
                  <span
                    className="hf-confirm-steplabel"
                    style={{ fontSize: 13, fontWeight: i === 0 ? 600 : 400, color: i === 0 ? C.navy : C.muted }}
                  >
                    {title}
                  </span>
                  {i < STEPS.length - 1 && <div style={{ width: 18, height: 1, backgroundColor: C.tan }} />}
                </div>
              ))}
            </div>
          </div>

          {/* Body */}
          <div style={{ padding: "26px 32px" }}>
            <h2 style={{ fontSize: 17, fontWeight: 700, color: "#1a1a1a", margin: "0 0 2px" }}>Confirm Details</h2>
            <p style={{ fontSize: 13, color: C.muted, margin: "0 0 18px" }}>Review and edit your information</p>

            {/* Loaded-from-provider banner */}
            <div
              style={{
                marginBottom: 18, padding: "10px 12px",
                backgroundColor: "#eef4f2", border: "1px solid #bbd5cc", borderRadius: 10,
                display: "flex", alignItems: "center", gap: 8,
              }}
            >
              <CheckIcon />
              <span style={{ fontSize: 13, color: "#2c5246" }}>
                Loaded from your provider. Review and update anything that's changed.
              </span>
            </div>

            <div className="hf-confirm-grid">
              {field({ k: "first_name", label: "First Name", placeholder: "First name" })}
              {field({ k: "last_name", label: "Last Name", placeholder: "Last name" })}
            </div>

            <div className="hf-confirm-grid" style={{ marginTop: 16 }}>
              {field({ k: "date_of_birth", label: "Date of Birth", type: "date", helper: "YYYY-MM-DD", format: "date_iso" })}
              <div>
                <label style={labelStyle}>Sex</label>
                <select value={values.gender} onChange={(e) => set("gender", e.target.value)} style={inputStyle("gender")}>
                  <option value="">—</option>
                  {GENDER_OPTIONS.map((g) => (
                    <option key={g} value={g}>{g}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="hf-confirm-grid" style={{ marginTop: 16 }}>
              {field({ k: "health_care_number", label: "Health Card Number", placeholder: "e.g. 1234 567 890", format: "health_card_ns" })}
              {field({ k: "phone", label: "Phone", type: "tel", placeholder: "(902) 555-1234", format: "phone_na" })}
            </div>

            <div style={{ marginTop: 16 }}>
              {field({ k: "email", label: "Email", type: "email", placeholder: "you@example.com", full: true, format: "email" })}
              {!emailValid && touched.email && (
                <p style={{ color: "#c25450", fontSize: 13, margin: "6px 2px 0" }}>That doesn't look like a valid email address.</p>
              )}
              {emailTypoSuggestion && (
                <button
                  type="button"
                  onClick={() => set("email", emailTypoSuggestion)}
                  style={{
                    background: "none", border: "none", padding: "4px 2px 0", cursor: "pointer",
                    color: C.sage, fontSize: 13, textDecoration: "underline", fontFamily: "system-ui",
                  }}
                >
                  Did you mean {emailTypoSuggestion}?
                </button>
              )}
            </div>

            <div className="hf-confirm-grid" style={{ marginTop: 16 }}>
              <div>
                <label style={labelStyle}>
                  Street Address
                  {editedKeys.includes("address") && (
                    <span style={{ marginLeft: 8, fontSize: 11, fontWeight: 600, color: C.edited }}>edited</span>
                  )}
                </label>
                <AddressAutocompleteInput
                  value={values.address}
                  onChange={(text) => set("address", text)}
                  onResolved={(rec) => {
                    set("address", rec.address as string);
                    if (rec.suite) set("suite", rec.suite);
                    if (rec.city) set("city", rec.city);
                    if (rec.province && PROVINCES.includes(rec.province)) set("province", rec.province);
                    if (rec.postal_code) set("postal_code", rec.postal_code);
                  }}
                  placeholder="Start typing your address…"
                  inputStyle={inputStyle("address")}
                />
              </div>
              {field({ k: "suite", label: "Suite / Unit (optional)", placeholder: "e.g. 4B" })}
            </div>

            <div className="hf-confirm-grid-3" style={{ marginTop: 16 }}>
              {field({ k: "city", label: "City", placeholder: "Halifax" })}
              <div>
                <label style={labelStyle}>Province</label>
                <select value={values.province} onChange={(e) => set("province", e.target.value)} style={inputStyle("province")}>
                  {PROVINCES.map((p) => (
                    <option key={p} value={p}>{p}</option>
                  ))}
                </select>
              </div>
              {field({ k: "postal_code", label: "Postal Code", placeholder: "B3H 1A1", format: "postal_ca" })}
            </div>

            {/* Insurance */}
            <Divider label="Insurance" />

            {/* Insurance card camera capture — sealed upload + on-device OCR.
                Extracted numbers land in the boxes below for the patient to
                confirm; already-typed values are never overwritten. */}
            <InsuranceCardCapture
              onExtracted={(candidates) => {
                // Route by canonical LEVEL (kills the group_id→plan_number
                // collapse): person→member, plan→plan (or division when the
                // insurer declares one). Never overwrites typed values; 0.5
                // confidence threshold. Legacy no-level candidates still route.
                const patch = routeCandidates(candidates, values, { showDivision });
                const applied = Object.keys(patch).length;
                if (applied) setValues((prev) => ({ ...prev, ...(patch as Record<string, string>) }));
                return applied;
              }}
            />

            {field({ k: "insurance_provider", label: "Insurance Provider", placeholder: "e.g. Blue Cross, Sun Life, Manulife", full: true })}

            <div className="hf-confirm-grid" style={{ marginTop: 16 }}>
              {field({
                k: "plan_number",
                label: insLabels.plan,
                placeholder: "Plan number",
                helper: insLabels.matched
                  ? "As printed on your benefits card."
                  : "Sometimes called policy, contract, or group number — on your benefits card.",
              })}
              {field({
                k: "insurance_id",
                label: insLabels.member,
                placeholder: "Member ID",
                helper: insLabels.matched
                  ? "As printed on your benefits card."
                  : "Sometimes called certificate, ID, or participant number.",
              })}
            </div>

            {/* Division / group number — only for insurers that declare a
                separate plan-level division id (most don't, so this stays
                hidden). Keeps a bare group # from being mistaken for the plan
                number on carriers that use both. */}
            {showDivision && (
              <div className="hf-confirm-grid" style={{ marginTop: 16 }}>
                {field({
                  k: "division_id",
                  label: insLabels.division,
                  placeholder: "Division / group number",
                  helper: "A secondary plan-level number some plans print in addition to the plan number.",
                })}
              </div>
            )}

            {/* Provincial drug coverage */}
            <div style={{ marginTop: 18 }}>
              <label style={labelStyle}>Do you also have provincial drug coverage?</label>
              <p style={{ color: C.muted, fontSize: 12, margin: "0 2px 10px", lineHeight: 1.4 }}>
                e.g. Pharmacare, Family Pharmacare, or a provincial seniors' plan — in addition to the insurance above.
              </p>
              <div style={{ display: "flex", gap: 10 }}>
                {["Yes", "No"].map((opt) => {
                  const selected = values.has_public_insurance === opt;
                  return (
                    <button
                      key={opt}
                      type="button"
                      onClick={() => set("has_public_insurance", opt)}
                      style={{
                        flex: "0 0 auto",
                        minWidth: 88,
                        padding: "10px 22px",
                        borderRadius: 999,
                        border: `1.5px solid ${selected ? C.navy : C.tan}`,
                        backgroundColor: selected ? C.navy : C.white,
                        color: selected ? "#fff" : C.navy,
                        fontSize: 15,
                        fontWeight: 600,
                        cursor: "pointer",
                      }}
                    >
                      {opt}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Plan member (policyholder) */}
            <Divider label="Plan member" />
            <div>
              <label style={labelStyle}>Are you the plan member (policyholder) on this insurance?</label>
              <p style={{ color: C.muted, fontSize: 12, margin: "0 2px 10px", lineHeight: 1.4 }}>
                The plan member is whoever the insurance is registered under. If that's someone else,
                we'll need their details so they can sign their part — they'll get their own secure link.
              </p>
              <select
                value={values.plan_member_relationship}
                onChange={(e) => set("plan_member_relationship", e.target.value)}
                style={inputStyle("plan_member_relationship")}
              >
                {RELATIONSHIP_OPTIONS.map((o) => (
                  <option key={o.v} value={o.v}>{o.label}</option>
                ))}
              </select>
            </div>

            {planMemberDistinct && (
              <div style={{ marginTop: 16 }}>
                <div
                  style={{
                    marginBottom: 14, padding: "10px 12px",
                    backgroundColor: C.editedBg, border: `1px solid ${C.tan}`, borderRadius: 10,
                  }}
                >
                  <span style={{ fontSize: 13, color: C.navy }}>
                    {pmTextOnly
                      ? "The plan member's signature is required on this form. Your clinician's office will text them a secure link to sign their part."
                      : "The plan member's signature is required on this form. We'll email them a secure link to sign their part after you continue."}
                  </span>
                </div>
                <div className="hf-confirm-grid">
                  {field({ k: "plan_member_first_name", label: "Plan member — First Name", placeholder: "First name" })}
                  {field({ k: "plan_member_last_name", label: "Plan member — Last Name", placeholder: "Last name" })}
                </div>
                <div className="hf-confirm-grid" style={{ marginTop: 16 }}>
                  {field({ k: "plan_member_dob", label: "Plan member — Date of Birth", type: "date", helper: "YYYY-MM-DD", format: "date_iso" })}
                  <div>
                    {field({ k: "plan_member_email", label: "Plan member — Email", type: "email", placeholder: "them@example.com", format: "email" })}
                    {!planMemberEmailValid && touched.plan_member_email && (
                      <p style={{ color: "#c25450", fontSize: 13, margin: "6px 2px 0" }}>That doesn't look like a valid email address.</p>
                    )}
                  </div>
                </div>
                <div className="hf-confirm-grid" style={{ marginTop: 16 }}>
                  {field({
                    k: "plan_member_phone", label: "Plan member — Mobile", type: "tel",
                    inputMode: "tel", placeholder: "(902) 555-1234", format: "phone_na",
                    helper: "An email or a mobile number — one is enough.",
                  })}
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div
            style={{
              padding: "16px 32px",
              backgroundColor: C.cream,
              borderTop: `1px solid ${C.border}`,
              display: "flex",
              justifyContent: "flex-end",
              alignItems: "center",
              gap: 12,
            }}
          >
            {!canContinue && (
              <span style={{ fontSize: 12, color: C.muted, textAlign: "right" }}>
                {!planMemberOk
                  ? "Plan member's name, date of birth, and an email or mobile number are required so they can sign."
                  : !formatsOk
                  ? `Please check ${badFormatKeys.map((k) => FIELD_LABELS[k] || k).join(", ")}.`
                  : "First name, last name, date of birth, and policy/group # are required."}
              </span>
            )}
            <button
              type="button"
              onClick={handleContinue}
              disabled={!canContinue}
              style={{
                display: "flex", alignItems: "center", gap: 6,
                padding: "11px 22px", fontSize: 15, fontWeight: 600,
                backgroundColor: canContinue ? C.navy : "#bbb",
                color: "#fff", border: "none", borderRadius: 10,
                cursor: canContinue ? "pointer" : "not-allowed",
              }}
            >
              Continue to your form →
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function CheckIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true" style={{ flexShrink: 0 }}>
      <circle cx="12" cy="12" r="10" fill="#6b9c8e" />
      <path d="M7 12.5l3.2 3.2L17 9" stroke="#fff" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function Divider({ label }: { label: React.ReactNode }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 12, margin: "26px 0 16px" }}>
      <span style={{ fontSize: 13, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: 0.6 }}>
        {label}
      </span>
      <div style={{ flex: 1, height: 1, backgroundColor: C.border }} />
    </div>
  );
}
